import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { FaMobileAlt, FaUniversity, FaWhatsapp, FaSave, FaInfoCircle, FaPaypal } from 'react-icons/fa';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ManualPaymentMethodsManager = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState({
    nequi: {
      enabled: true,
      name: 'Nequi',
      account_number: '3192467882',
      account_holder: 'Deisy Carolina Pérez',
      instructions: ''
    },
    bancolombia: {
      enabled: true,
      name: 'Bancolombia',
      account_number: '',
      account_holder: 'Deisy Carolina Pérez',
      account_type: 'Ahorros',
      instructions: ''
    },
    transferencia: {
      enabled: true,
      name: 'Transferencia Directa',
      instructions: ''
    },
    paypal: {
      enabled: true,
      name: 'PayPal',
      email: '',
      paypal_link: '',
      instructions: ''
    },
    whatsapp_number: '3195825712',
    whatsapp_link: 'https://wa.link/78phcb',
    activation_note: 'Después de enviar tu comprobante se activa manualmente la cuenta. Si quieres activación inmediata usa Wompi o PSE'
  });

  useEffect(() => {
    fetchPaymentMethods();
  }, []);

  const fetchPaymentMethods = async () => {
    try {
      const response = await axios.get(`${API}/admin/site-config`, { withCredentials: false });
      if (response.data.manual_payment_methods) {
        setPaymentMethods(prev => ({
          ...prev,
          ...response.data.manual_payment_methods
        }));
      }
    } catch (error) {
      console.error('Error fetching payment methods:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/site-config`, {
        manual_payment_methods: paymentMethods
      }, { withCredentials: false });
      toast.success('Métodos de pago actualizados correctamente');
    } catch (error) {
      console.error('Error saving payment methods:', error);
      toast.error('Error al guardar los cambios');
    } finally {
      setSaving(false);
    }
  };

  const updateMethod = (methodId, field, value) => {
    setPaymentMethods(prev => ({
      ...prev,
      [methodId]: {
        ...prev[methodId],
        [field]: value
      }
    }));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Nota informativa */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-4">
          <div className="flex items-start gap-3">
            <FaInfoCircle className="text-blue-500 text-xl flex-shrink-0 mt-1" />
            <div>
              <p className="text-blue-800 font-medium">Métodos de Pago Manuales</p>
              <p className="text-blue-600 text-sm">
                Estos son los métodos donde el cliente paga manualmente y envía comprobante. 
                Los pagos con Wompi, MercadoPago y PayPal son automáticos.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* WhatsApp y Nota de Activación */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FaWhatsapp className="text-green-500" />
            Configuración General
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Número de WhatsApp (fallback)</Label>
              <Input
                value={paymentMethods.whatsapp_number || ''}
                onChange={(e) => setPaymentMethods(prev => ({ ...prev, whatsapp_number: e.target.value }))}
                placeholder="3195825712"
                data-testid="whatsapp-number-input"
              />
              <p className="text-xs text-gray-500 mt-1">Sin espacios ni guiones (se usa si no hay link corto)</p>
            </div>
            <div>
              <Label>🔗 Link corto de WhatsApp (RECOMENDADO)</Label>
              <Input
                value={paymentMethods.whatsapp_link || ''}
                onChange={(e) => setPaymentMethods(prev => ({ ...prev, whatsapp_link: e.target.value }))}
                placeholder="https://wa.link/142sxe"
                data-testid="whatsapp-link-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                Créalo en <a href="https://web.whatsapp.com/" target="_blank" rel="noreferrer" className="underline">wa.link</a> o WhatsApp Business.
                Este link es más confiable que wa.me con el número.
              </p>
            </div>
          </div>
          <div>
            <Label>Nota sobre activación manual</Label>
            <Textarea
              value={paymentMethods.activation_note || ''}
              onChange={(e) => setPaymentMethods(prev => ({ ...prev, activation_note: e.target.value }))}
              placeholder="Mensaje que verá el cliente sobre la activación manual..."
              rows={3}
            />
          </div>
        </CardContent>
      </Card>

      {/* Nequi */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FaMobileAlt className="text-purple-500" />
              Nequi
            </div>
            <Switch
              checked={paymentMethods.nequi?.enabled ?? true}
              onCheckedChange={(checked) => updateMethod('nequi', 'enabled', checked)}
            />
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Número de Nequi</Label>
              <Input
                value={paymentMethods.nequi?.account_number || ''}
                onChange={(e) => updateMethod('nequi', 'account_number', e.target.value)}
                placeholder="3192467882"
              />
            </div>
            <div>
              <Label>Titular de la cuenta</Label>
              <Input
                value={paymentMethods.nequi?.account_holder || ''}
                onChange={(e) => updateMethod('nequi', 'account_holder', e.target.value)}
                placeholder="Deisy Carolina Pérez"
              />
            </div>
          </div>
          <div>
            <Label>Instrucciones adicionales (opcional)</Label>
            <Textarea
              value={paymentMethods.nequi?.instructions || ''}
              onChange={(e) => updateMethod('nequi', 'instructions', e.target.value)}
              placeholder="Instrucciones adicionales para el pago por Nequi..."
              rows={2}
            />
          </div>
        </CardContent>
      </Card>

      {/* Bancolombia */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FaUniversity className="text-yellow-500" />
              Bancolombia
            </div>
            <Switch
              checked={paymentMethods.bancolombia?.enabled ?? true}
              onCheckedChange={(checked) => updateMethod('bancolombia', 'enabled', checked)}
            />
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Número de Cuenta</Label>
              <Input
                value={paymentMethods.bancolombia?.account_number || ''}
                onChange={(e) => updateMethod('bancolombia', 'account_number', e.target.value)}
                placeholder="Número de cuenta"
              />
            </div>
            <div>
              <Label>Tipo de Cuenta</Label>
              <select
                className="w-full h-10 px-3 rounded-md border border-gray-300 bg-white"
                value={paymentMethods.bancolombia?.account_type || 'Ahorros'}
                onChange={(e) => updateMethod('bancolombia', 'account_type', e.target.value)}
              >
                <option value="Ahorros">Ahorros</option>
                <option value="Corriente">Corriente</option>
              </select>
            </div>
            <div>
              <Label>Titular de la cuenta</Label>
              <Input
                value={paymentMethods.bancolombia?.account_holder || ''}
                onChange={(e) => updateMethod('bancolombia', 'account_holder', e.target.value)}
                placeholder="Deisy Carolina Pérez"
              />
            </div>
          </div>
          <div>
            <Label>Instrucciones adicionales (opcional)</Label>
            <Textarea
              value={paymentMethods.bancolombia?.instructions || ''}
              onChange={(e) => updateMethod('bancolombia', 'instructions', e.target.value)}
              placeholder="Instrucciones adicionales para la transferencia..."
              rows={2}
            />
          </div>
        </CardContent>
      </Card>

      {/* Transferencia Directa */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FaUniversity className="text-gray-500" />
              Transferencia Directa (Otros Bancos)
            </div>
            <Switch
              checked={paymentMethods.transferencia?.enabled ?? true}
              onCheckedChange={(checked) => updateMethod('transferencia', 'enabled', checked)}
            />
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div>
            <Label>Instrucciones</Label>
            <Textarea
              value={paymentMethods.transferencia?.instructions || ''}
              onChange={(e) => updateMethod('transferencia', 'instructions', e.target.value)}
              placeholder="Instrucciones para transferencias desde otros bancos..."
              rows={3}
            />
          </div>
        </CardContent>
      </Card>

      {/* PayPal */}
      <Card data-testid="admin-paypal-card">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FaPaypal className="text-blue-500" />
              PayPal (Manual)
            </div>
            <Switch
              data-testid="paypal-enabled-switch"
              checked={paymentMethods.paypal?.enabled ?? true}
              onCheckedChange={(checked) => updateMethod('paypal', 'enabled', checked)}
            />
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Correo de PayPal</Label>
              <Input
                data-testid="paypal-email-input"
                type="email"
                value={paymentMethods.paypal?.email || ''}
                onChange={(e) => updateMethod('paypal', 'email', e.target.value)}
                placeholder="tucorreo@paypal.com"
              />
            </div>
            <div>
              <Label>Link de PayPal.me (opcional)</Label>
              <Input
                data-testid="paypal-link-input"
                value={paymentMethods.paypal?.paypal_link || ''}
                onChange={(e) => updateMethod('paypal', 'paypal_link', e.target.value)}
                placeholder="https://paypal.me/tuusuario"
              />
            </div>
          </div>
          <div>
            <Label>Instrucciones adicionales (opcional)</Label>
            <Textarea
              value={paymentMethods.paypal?.instructions || ''}
              onChange={(e) => updateMethod('paypal', 'instructions', e.target.value)}
              placeholder="Envía el pago como Amigos y Familia para evitar comisiones..."
              rows={2}
            />
          </div>
        </CardContent>
      </Card>

      {/* Botón Guardar */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-gradient-to-r from-pink-500 to-purple-500 text-white px-8"
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Guardando...
            </>
          ) : (
            <>
              <FaSave className="mr-2" />
              Guardar Cambios
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default ManualPaymentMethodsManager;
